// Eigentümerliste (Handoff Eigentümer §6.1): Zuordnungen je Eigentümer, Status, Status- und
// Rollen-Filter mit Zählern, Suche, Kennzahlen, Wechsel & Mandatsende. Reine Funktionen – testbar.

import { istWohnend } from '../dashboard/leerstand';
import { aktuelleVerguetung, type VerguetungEintrag } from './verwaltung';
import { mieteAktuell } from '../immobilien/einheitDetail';
import { deDatum, type MieterDetailRoh } from '../immobilien/objektDetail';
import { objektName, type ObjektKopfRoh } from '../immobilien/objektKopf';
import { normalisiereSuche } from '../immobilien/suche';
import { verwaltungsartVonObjekt } from '../immobilien/sidebarNav';
import { RUECKSTAND_SCHWELLE } from '../mietkonto/mietkonto';
import { eigentuemerAnzeigename, istFirma, type EigentuemerNameFelder } from './anzeige';
import { rolleFuer, rollenWerte, type RollenWerte, type Zuordnung, type ZuordnungEinheit } from './rollen';

export interface EigentuemerRoh extends EigentuemerNameFelder {
  id: string;
  email?: string | null;
  telefon?: string | null;
  mobil?: string | null;
  status?: string | null;
  vertragsstart?: string | null;
  vertragsende?: string | null;
  stadt?: string | null;
  verguetung_je_einheit?: number | string | null;
}
export interface ObjektEigRoh { objekt_id: string; eigentuemer_id: string; vertrag_ab: string | null; vertrag_bis: string | null }
export interface EinheitEigRoh {
  einheit_id: string; eigentuemer_id: string; mea: number | string | null; hausgeld_vorschuss: number | string | null;
  ruecklage_vorschuss: number | string | null; sev_status: string | null; vertrag_ab: string | null; vertrag_bis: string | null;
}
export interface EinheitRoh { id: string; objekt_id: string | null; bezeichnung: string | null; wohnflaeche: number | string | null }
export type ObjektRoh = ObjektKopfRoh & {
  objekttyp?: string | null;
  /** Verwalterbestellung der WEG (§ 26 WEG) */
  weg_bestellt_seit?: string | null; weg_bestellt_bis?: string | null; weg_beirat?: string | null;
};
export type MieterRoh = MieterDetailRoh & { objekt_id: string | null };

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : 0;
};
const zahlOderNull = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number.isFinite(zahl(v)) ? zahl(v) : null);
const datum = (v: string | null | undefined): string | null => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null);
/** Zuordnung läuft noch oder beginnt erst (Ende leer oder ≥ heute). */
const laeuft = (bis: string | null, heute: string): boolean => !bis || bis >= heute;

export interface ZuordnungsDaten {
  objekte: ReadonlyMap<string, ObjektRoh>;
  einheitenJeObjekt: ReadonlyMap<string, EinheitRoh[]>;
  einheit: ReadonlyMap<string, EinheitRoh>;
  /** laufende Mieter je Einheit */
  mieterJeEinheit: ReadonlyMap<string, MieterRoh[]>;
}

export function zuordnungsDaten(objekte: readonly ObjektRoh[], einheiten: readonly EinheitRoh[], mieter: readonly MieterRoh[], heute: string): ZuordnungsDaten {
  const jeObjekt = new Map<string, EinheitRoh[]>();
  for (const e of einheiten) if (e.objekt_id) jeObjekt.set(e.objekt_id, [...(jeObjekt.get(e.objekt_id) ?? []), e]);
  const jeEinheit = new Map<string, MieterRoh[]>();
  for (const m of mieter) {
    if (!m.einheit_id || !istWohnend(m.status)) continue;
    if (m.mietbeginn && m.mietbeginn.slice(0, 10) > heute) continue;
    if (m.mietende && m.mietende.slice(0, 10) < heute) continue;
    jeEinheit.set(m.einheit_id, [...(jeEinheit.get(m.einheit_id) ?? []), m]);
  }
  return { objekte: new Map(objekte.map(o => [o.id, o])), einheitenJeObjekt: jeObjekt, einheit: new Map(einheiten.map(e => [e.id, e])), mieterJeEinheit: jeEinheit };
}

function zuEinheit(e: EinheitRoh, d: ZuordnungsDaten, heute: string, ee?: EinheitEigRoh): ZuordnungEinheit {
  const mieter = d.mieterJeEinheit.get(e.id) ?? [];
  return {
    id: e.id,
    bezeichnung: (e.bezeichnung ?? '').trim() || 'Ohne Bezeichnung',
    flaeche: zahl(e.wohnflaeche),
    hausgeld: ee ? Math.round((zahl(ee.hausgeld_vorschuss) + zahl(ee.ruecklage_vorschuss)) * 100) / 100 : 0,
    mea: ee && ee.mea !== null && ee.mea !== '' ? zahl(ee.mea) : null,
    miete: mieter.length ? mieteAktuell(mieter, heute).gesamt : 0,
    vermietet: mieter.length > 0,
  };
}

/**
 * Zuordnungen eines Eigentümers (nur laufende): Einheiten-Eigentum (einheit_eigentuemer) je Objekt und Rolle;
 * Eigentum am ganzen Objekt (objekt_eigentuemer) nur, wenn es in dem Objekt keine Einheiten-Zuordnung gibt.
 * Bei WEG/SEV-Objekten ohne Einheiten-Zuordnung bleiben die Einheiten leer (nicht alle Einheiten des Objekts).
 */
export function zuordnungenVon(
  eigId: string, objektEig: readonly ObjektEigRoh[], einheitEig: readonly EinheitEigRoh[], d: ZuordnungsDaten, heute: string,
): Zuordnung[] {
  const gruppen = new Map<string, Zuordnung>();
  for (const ee of einheitEig) {
    if (ee.eigentuemer_id !== eigId || !laeuft(datum(ee.vertrag_bis), heute)) continue;
    const e = d.einheit.get(ee.einheit_id);
    if (!e?.objekt_id) continue;
    const o = d.objekte.get(e.objekt_id);
    const rolle = rolleFuer(o ? verwaltungsartVonObjekt(o) : null, ee.sev_status);
    const key = `${e.objekt_id}|${rolle}`;
    const z = gruppen.get(key) ?? { objektId: e.objekt_id, objektName: o ? objektName(o) : 'Objekt', rolle, ganzesObjekt: false, einheiten: [], ab: datum(ee.vertrag_ab), bis: datum(ee.vertrag_bis) };
    z.einheiten.push(zuEinheit(e, d, heute, ee));
    const ab = datum(ee.vertrag_ab);
    if (ab && (!z.ab || ab < z.ab)) z.ab = ab;
    gruppen.set(key, z);
  }
  const mitEinheiten = new Set([...gruppen.values()].map(z => z.objektId));
  for (const oe of objektEig) {
    if (oe.eigentuemer_id !== eigId || mitEinheiten.has(oe.objekt_id) || !laeuft(datum(oe.vertrag_bis), heute)) continue;
    const o = d.objekte.get(oe.objekt_id);
    const va = o ? verwaltungsartVonObjekt(o) : null;
    const rolle = rolleFuer(va, null);
    const ganz = rolle === 'MV';
    gruppen.set(`${oe.objekt_id}|${rolle}`, {
      objektId: oe.objekt_id, objektName: o ? objektName(o) : 'Objekt', rolle, ganzesObjekt: ganz,
      einheiten: ganz ? (d.einheitenJeObjekt.get(oe.objekt_id) ?? []).map(e => zuEinheit(e, d, heute)) : [],
      ab: datum(oe.vertrag_ab), bis: datum(oe.vertrag_bis),
    });
  }
  return [...gruppen.values()].sort((a, b) => a.objektName.localeCompare(b.objektName, 'de', { numeric: true }));
}

export type EigStatus = 'ehemalig' | 'uebernahme' | 'endet' | 'saldo' | 'aktiv';
export const EIG_STATUS: Record<EigStatus, { label: string; ton: 'neutral' | 'info' | 'warn' | 'bad' | 'ok' }> = {
  ehemalig: { label: 'Ehemalig', ton: 'neutral' },
  uebernahme: { label: 'Übernahme geplant', ton: 'info' },
  endet: { label: 'Endet', ton: 'warn' },
  saldo: { label: 'Offener Saldo', ton: 'bad' },
  aktiv: { label: 'Aktiv', ton: 'ok' },
};

export function istInaktivStatus(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return s === 'inaktiv' || s === 'ehemalig';
}

/** Status in fester Reihenfolge: Ehemalig → Übernahme geplant → Endet → Offener Saldo → Aktiv. */
export function eigStatus(p: { status: string | null | undefined; beginn: string | null; endetAm: string | null; saldo: number | null; hatteZuordnung: boolean; hatLaufende: boolean }, heute: string): EigStatus {
  if (istInaktivStatus(p.status) || (p.hatteZuordnung && !p.hatLaufende)) return 'ehemalig';
  if (p.beginn && p.beginn > heute) return 'uebernahme';
  if (p.endetAm && p.endetAm >= heute) return 'endet';
  if (p.saldo !== null && p.saldo < -RUECKSTAND_SCHWELLE) return 'saldo';
  return 'aktiv';
}

export type PortalZustand = 'aktiv' | 'eingeladen' | 'gesperrt' | 'kein';
export const PORTAL_ANZEIGE: Record<PortalZustand, { label: string; ton: 'ok' | 'warn' | 'bad' | 'neutral'; farbe: string }> = {
  aktiv: { label: 'Portal aktiv', ton: 'ok', farbe: 'var(--ok)' },
  eingeladen: { label: 'Eingeladen', ton: 'warn', farbe: 'var(--warn)' },
  gesperrt: { label: 'Gesperrt', ton: 'bad', farbe: 'var(--bad)' },
  kein: { label: 'Kein Portal', ton: 'neutral', farbe: 'var(--ink3)' },
};

export interface EigZeile {
  id: string;
  name: string;
  firma: boolean;
  email: string;
  telefon: string;
  zuordnungen: Zuordnung[];
  werte: RollenWerte;
  objektIds: string[];
  /** „Alte Straße 17“ bzw. „2 Objekte“ */
  objektText: string;
  /** „MV · ganzes Objekt · 4 Einheiten“ */
  rollenText: string;
  status: EigStatus;
  beginn: string | null;
  endetAm: string | null;
  portal: PortalZustand;
  saldo: number | null;
  suchtext: string;
}

export interface ZeilenEingabe {
  eigentuemer: readonly EigentuemerRoh[];
  objektEig: readonly ObjektEigRoh[];
  einheitEig: readonly EinheitEigRoh[];
  daten: ZuordnungsDaten;
  portal: ReadonlyMap<string, string | null>;
  /** Kontostand je Eigentümer aus dem gemeinsamen Mietkonto; null = nicht eingerichtet */
  salden: ReadonlyMap<string, number> | null;
  heute: string;
  /** Vergütungshistorie je Eigentümer (eigentuemer_verguetung); fehlt sie, gilt verguetung_je_einheit */
  verguetungen?: ReadonlyMap<string, readonly VerguetungEintrag[]>;
}

export function baueEigZeilen(e: ZeilenEingabe): EigZeile[] {
  const oeJe = new Map<string, ObjektEigRoh[]>();
  for (const r of e.objektEig) oeJe.set(r.eigentuemer_id, [...(oeJe.get(r.eigentuemer_id) ?? []), r]);
  const eeJe = new Map<string, EinheitEigRoh[]>();
  for (const r of e.einheitEig) eeJe.set(r.eigentuemer_id, [...(eeJe.get(r.eigentuemer_id) ?? []), r]);
  return e.eigentuemer.map(eig => {
    const oe = oeJe.get(eig.id) ?? [];
    const ee = eeJe.get(eig.id) ?? [];
    const zuordnungen = zuordnungenVon(eig.id, oe, ee, e.daten, e.heute);
    // Verwaltervergütung je Einheit (eigentuemer.verguetung_je_einheit); leer = nicht vereinbart
    const werte = rollenWerte(zuordnungen, aktuelleVerguetung(e.verguetungen?.get(eig.id) ?? [], e.heute, zahlOderNull(eig.verguetung_je_einheit)));
    const objektIds = [...new Set(zuordnungen.map(z => z.objektId))];
    const anfaenge = [datum(eig.vertragsstart ?? null), ...zuordnungen.map(z => z.ab)].filter((x): x is string => !!x).sort();
    const enden = [datum(eig.vertragsende ?? null), ...zuordnungen.map(z => z.bis)].filter((x): x is string => !!x && x >= e.heute).sort();
    const beginnLaufend = zuordnungen.length && zuordnungen.every(z => z.ab && z.ab > e.heute) ? zuordnungen.map(z => z.ab as string).sort()[0] ?? null : null;
    const beginn = beginnLaufend ?? (datum(eig.vertragsstart ?? null) && (datum(eig.vertragsstart ?? null) as string) > e.heute ? datum(eig.vertragsstart ?? null) : anfaenge[0] ?? null);
    const saldo = e.salden ? e.salden.get(eig.id) ?? null : null;
    const email = (eig.email ?? '').trim();
    const name = eigentuemerAnzeigename(eig);
    const einheitenText = werte.nEinheiten === 1 ? '1 Einheit' : `${werte.nEinheiten} Einheiten`;
    const ganz = zuordnungen.length > 0 && zuordnungen.every(z => z.ganzesObjekt);
    const status = eigStatus({
      status: eig.status, beginn, endetAm: enden[0] ?? null, saldo,
      hatteZuordnung: oe.length + ee.length > 0, hatLaufende: zuordnungen.length > 0,
    }, e.heute);
    const p = email ? e.portal.get(email.toLowerCase()) : undefined;
    return {
      id: eig.id,
      name,
      firma: istFirma(eig),
      email,
      telefon: (eig.telefon ?? '').trim() || (eig.mobil ?? '').trim(),
      zuordnungen,
      werte,
      objektIds,
      objektText: objektIds.length === 0 ? 'Kein Objekt zugeordnet' : objektIds.length === 1 ? zuordnungen[0]?.objektName ?? '' : `${objektIds.length} Objekte`,
      rollenText: [werte.rollenL, ganz ? 'ganzes Objekt' : '', werte.nEinheiten ? einheitenText : ''].filter(Boolean).join(' · '),
      status,
      beginn,
      endetAm: enden[0] ?? null,
      portal: p === 'Aktiv' ? 'aktiv' : p === 'Inaktiv' ? 'gesperrt' : 'kein',
      saldo,
      suchtext: normalisiereSuche(`${name} ${eig.firma_name ?? ''} ${email} ${eig.stadt ?? ''} ${zuordnungen.map(z => {
        const o = e.daten.objekte.get(z.objektId);
        return `${z.objektName} ${o?.strasse ?? ''} ${(o?.stadt ?? '') || (o?.ort ?? '')}`;
      }).join(' ')}`),
    };
  });
}

export type StatusFilter = 'aktiv' | 'saldo' | 'wechsel' | 'ehemalig';
export type RollenFilter = 'alle' | 'MV' | 'WEG' | 'SEV';
export const STATUS_FILTER: readonly { key: StatusFilter; label: string }[] = [
  { key: 'aktiv', label: 'Aktiv' }, { key: 'saldo', label: 'Offener Saldo' }, { key: 'wechsel', label: 'Wechsel & Mandatsende' }, { key: 'ehemalig', label: 'Ehemalig' },
];
export const ROLLEN_FILTER: readonly { key: RollenFilter; label: string }[] = [
  { key: 'alle', label: 'Alle Rollen' }, { key: 'MV', label: 'Mietverwaltung' }, { key: 'WEG', label: 'WEG' }, { key: 'SEV', label: 'SEV' },
];
export function istStatusFilter(v: string | null): v is StatusFilter { return STATUS_FILTER.some(f => f.key === v); }
export function istRollenFilter(v: string | null): v is RollenFilter { return ROLLEN_FILTER.some(f => f.key === v); }

export function passtStatus(z: Pick<EigZeile, 'status' | 'saldo'>, f: StatusFilter): boolean {
  switch (f) {
    case 'aktiv': return z.status !== 'ehemalig';
    case 'saldo': return z.status !== 'ehemalig' && z.saldo !== null && z.saldo < -RUECKSTAND_SCHWELLE;
    case 'wechsel': return z.status === 'uebernahme' || z.status === 'endet';
    case 'ehemalig': return z.status === 'ehemalig';
  }
}

export function passtRolle(z: Pick<EigZeile, 'werte'>, r: RollenFilter): boolean {
  return r === 'alle' || (r === 'MV' ? z.werte.hasMV : r === 'WEG' ? z.werte.hasWEG : z.werte.hasSEV);
}

export function basisZeilen<T extends Pick<EigZeile, 'objektIds' | 'suchtext'>>(zeilen: readonly T[], objekt: string, suche: string): T[] {
  const q = normalisiereSuche(suche);
  const woerter = q ? q.split(' ') : [];
  return zeilen.filter(z => (objekt === 'alle' || z.objektIds.includes(objekt)) && woerter.every(w => z.suchtext.includes(w)));
}

/** Status-Zähler gelten für die gewählte Rolle, Rollen-Zähler für den gewählten Status (wie im Prototyp). */
export function zaehler(basis: readonly Pick<EigZeile, 'status' | 'saldo' | 'werte'>[], status: StatusFilter, rolle: RollenFilter): { status: Record<StatusFilter, number>; rollen: Record<RollenFilter, number> } {
  const s: Record<StatusFilter, number> = { aktiv: 0, saldo: 0, wechsel: 0, ehemalig: 0 };
  const r: Record<RollenFilter, number> = { alle: 0, MV: 0, WEG: 0, SEV: 0 };
  for (const z of basis) {
    if (passtRolle(z, rolle)) for (const f of STATUS_FILTER) if (passtStatus(z, f.key)) s[f.key] += 1;
    if (passtStatus(z, status)) for (const f of ROLLEN_FILTER) if (passtRolle(z, f.key)) r[f.key] += 1;
  }
  return { status: s, rollen: r };
}

export interface EigKennzahlen {
  aktiv: number;
  /** Summe der hinterlegten Verwaltervergütungen; null = bei keinem verwalteten Eigentümer hinterlegt */
  verguetung: number | null;
  /** verwaltete Eigentümer (MV/SEV) ohne hinterlegte Vergütung */
  ohneVerguetung: number;
  mv: number; weg: number; sev: number; uebernahme: number;
  objekte: number; einheiten: number;
  offenerSaldo: number | null; saldoAnzahl: number;
  portalAktiv: number; portalEingeladen: number; portalQuote: number;
}

export function eigKennzahlen(zeilen: readonly EigZeile[], kontoBereit: boolean): EigKennzahlen {
  const nichtEhemalig = zeilen.filter(z => z.status !== 'ehemalig');
  const laufend = nichtEhemalig.filter(z => z.status !== 'uebernahme');
  const imSaldo = nichtEhemalig.filter(z => z.saldo !== null && z.saldo < -RUECKSTAND_SCHWELLE);
  const verg = laufend.map(z => z.werte.vergM).filter(v => v !== 0);
  const portalAktiv = nichtEhemalig.filter(z => z.portal === 'aktiv').length;
  return {
    aktiv: laufend.length,
    verguetung: verg.length === 0 ? 0 : verg.every(v => v === null) ? null : Math.round(verg.reduce<number>((s, v) => s + (v ?? 0), 0) * 100) / 100,
    ohneVerguetung: verg.filter(v => v === null).length,
    mv: laufend.filter(z => z.werte.hasMV).length,
    weg: laufend.filter(z => z.werte.hasWEG).length,
    sev: laufend.filter(z => z.werte.hasSEV).length,
    uebernahme: nichtEhemalig.length - laufend.length,
    objekte: new Set(laufend.flatMap(z => z.objektIds)).size,
    einheiten: new Set(laufend.flatMap(z => z.zuordnungen.flatMap(x => x.einheiten.map(e => e.id)))).size,
    offenerSaldo: kontoBereit ? Math.round(imSaldo.reduce((s, z) => s + (z.saldo ?? 0), 0) * 100) / 100 : null,
    saldoAnzahl: imSaldo.length,
    portalAktiv,
    portalEingeladen: nichtEhemalig.filter(z => z.portal === 'eingeladen').length,
    portalQuote: nichtEhemalig.length ? Math.round((portalAktiv / nichtEhemalig.length) * 100) : 0,
  };
}

export interface EigWechsel { id: string; art: 'uebernahme' | 'abgabe'; name: string; objekt: string; datum: string }

/** Die nächsten Übernahmen und Abgaben/Mandatsenden ab heute. */
export function naechsteWechsel(zeilen: readonly EigZeile[], heute: string, anzahl = 3): EigWechsel[] {
  const liste: EigWechsel[] = [];
  for (const z of zeilen) {
    if (z.status === 'ehemalig') continue;
    const objekt = z.zuordnungen[0]?.objektName ?? '';
    if (z.status === 'uebernahme' && z.beginn) liste.push({ id: z.id, art: 'uebernahme', name: z.name, objekt, datum: z.beginn });
    else if (z.endetAm && z.endetAm >= heute) liste.push({ id: z.id, art: 'abgabe', name: z.name, objekt, datum: z.endetAm });
  }
  return liste.sort((a, b) => a.datum.localeCompare(b.datum) || a.name.localeCompare(b.name, 'de')).slice(0, anzahl);
}

/** Anzahl nicht ehemaliger Eigentümer je Objekt (Objekt-Dropdown). */
export function eigentuemerJeObjekt(zeilen: readonly Pick<EigZeile, 'status' | 'objektIds'>[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const z of zeilen) if (z.status !== 'ehemalig') for (const id of z.objektIds) m.set(id, (m.get(id) ?? 0) + 1);
  return m;
}

export function statusText(z: Pick<EigZeile, 'status' | 'endetAm'>): string {
  return z.status === 'endet' && z.endetAm ? `Endet ${deDatum(z.endetAm)}` : EIG_STATUS[z.status].label;
}
